import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

// Components
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';

// EmailJS configuration
// Replace these with your actual EmailJS credentials
const EMAILJS_SERVICE_ID = "service_ucdcxq6"; // Your EmailJS service ID (Gmail)
const EMAILJS_TEMPLATE_ID = "template_eysm6pq"; // Your EmailJS template ID
const EMAILJS_PUBLIC_KEY = "Fd6OIGExAAavTA8TP"; // Your EmailJS public key

const ContactPage = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError('');
    
    // Send email using EmailJS
    emailjs.sendForm(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      form.current,
      EMAILJS_PUBLIC_KEY
    )
    .then((result) => {
      console.log('Email sent successfully:', result.text);
      setFormSubmitted(true);
      setLoading(false);
      
      // Reset form after submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Reset form submission status after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    })
    .catch((error) => {
      console.error('Failed to send email:', error);
      
      // Check for specific error types
      if (error.status === 412) {
        setSubmitError('Email service configuration error. Please contact the site administrator.');
      } else if (error.status === 400) {
        setSubmitError('Invalid email format. Please check your information and try again.');
      } else {
        setSubmitError('Failed to send message. Please try again or contact us directly at your-email@example.com');
      }
      
      setLoading(false);
      
      // Reset error message after 8 seconds
      setTimeout(() => {
        setSubmitError('');
      }, 8000);
    });
  };
  
  return (
    <PageContainer>
      <HeroSection>
        <Container>
          <SectionTitle 
            title="Contact Us" 
            subtitle="We'd love to hear from you"
          />
        </Container>
      </HeroSection>
      
      <Section>
        <Container>
          <ContactGrid>
            <ContactInfo>
              <InfoTitle>Get in Touch</InfoTitle>
              <InfoText>
                Have questions about our products or need assistance with your order? 
                Our team is here to help. Reach out to us using any of the methods below.
              </InfoText>
              
              <ContactDetails>
                <ContactItem>
                  <ContactIcon>
                    <FaPhone />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Phone</ContactLabel>
                    <ContactValue>505-363-6058</ContactValue>
                  </ContactText>
                </ContactItem>
                
                <ContactItem>
                  <ContactIcon>
                    <FaEnvelope />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Email</ContactLabel>
                    <ContactValue>info@mastercollectorsforge.com</ContactValue>
                  </ContactText>
                </ContactItem>
                
                <ContactItem>
                  <ContactIcon>
                    <FaMapMarkerAlt />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Address</ContactLabel>
                    <ContactValue>
                      Belen, New Mexico, 87002
                    </ContactValue>
                  </ContactText>
                </ContactItem>
                
                <ContactItem>
                  <ContactIcon>
                    <FaClock />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Business Hours</ContactLabel>
                    <ContactValue>
                      24/7 Online Service
                    </ContactValue>
                  </ContactText>
                </ContactItem>
              </ContactDetails>
            </ContactInfo>
            
            <ContactForm ref={form} onSubmit={handleSubmit}>
              <FormTitle>Send Us a Message</FormTitle>
              
              {formSubmitted ? (
                <SuccessMessage>
                  <SuccessIcon>
                    <FaCheckCircle />
                  </SuccessIcon>
                  <SuccessText>
                    <h3>Thank You!</h3>
                    <p>Your message has been sent successfully. We'll get back to you as soon as possible.</p>
                  </SuccessText>
                </SuccessMessage>
              ) : submitError ? (
                <ErrorMessage>
                  <ErrorIcon>
                    <FaExclamationCircle />
                  </ErrorIcon>
                  <ErrorText>
                    <h3>Oops!</h3>
                    <p>{submitError}</p>
                  </ErrorText>
                </ErrorMessage>
              ) : (
                <FormFields>
                  <FormGroup>
                    <FormLabel htmlFor="name">Your Name</FormLabel>
                    <FormInput
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="email">Email Address</FormLabel>
                    <FormInput
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="subject">Subject</FormLabel>
                    <FormInput
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="message">Message</FormLabel>
                    <FormTextarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="large"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </FormFields>
              )}
            </ContactForm>
          </ContactGrid>
        </Container>
      </Section>
      
      <MapSection>
        <MapContainer>
          <iframe
            title="Master Collectors Forge Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53301.80371489355!2d-106.82724534179686!3d34.65862710000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8722f5f9d0a9f457%3A0x4b42fcac8e2e5c01!2sBelen%2C%20NM%2087002!5e0!3m2!1sen!2sus!4v1711395770149!5m2!1sen!2sus"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </MapContainer>
      </MapSection>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div``;

const HeroSection = styled.section`
  background: ${({ theme }) => theme.gradients.darkBlue};
  padding: 3rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Section = styled.section`
  padding: 5rem 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background-color: ${({ theme }) => theme.colors.background.light};
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const InfoTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ContactText = styled.div``;

const ContactLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const ContactValue = styled.p`
  font-size: 1rem;
  line-height: 1.5;
`;

const ContactForm = styled.form`
  background-color: white;
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
`;

const FormInput = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.light};
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.8rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.light};
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background-color: #e6f7e6;
  border: 1px solid #c3e6c3;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1.5rem;
`;

const SuccessIcon = styled.div`
  color: #2e7d32;
  font-size: 2rem;
  flex-shrink: 0;
`;

const SuccessText = styled.div`
  h3 {
    color: #2e7d32;
    margin-bottom: 0.5rem;
  }
  
  p {
    line-height: 1.5;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1.5rem;
`;

const ErrorIcon = styled.div`
  color: #c62828;
  font-size: 2rem;
  flex-shrink: 0;
`;

const ErrorText = styled.div`
  h3 {
    color: #c62828;
    margin-bottom: 0.5rem;
  }
  
  p {
    line-height: 1.5;
  }
`;

const MapSection = styled.section`
  padding: 0;
  margin-top: 5rem;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  
  iframe {
    width: 100%;
    height: 100%;
  }
`;

export default ContactPage;
